/**
 * Vault Pipeline Test Suite
 * 
 * Tests the complete write → read flow for the unified vault.
 * 
 * Usage: window.testVaultPipeline()
 */

import vaultService from '../services/vaultService';
import vaultWriterService from '../services/vaultWriterService';
import { supabase } from '../lib/supabase';

const TEST_PREFIX = '[VAULT_TEST]';

/**
 * Test 1: Write and Read Flow
 */
async function testWriteReadFlow(userId) {
  console.log(`${TEST_PREFIX} Test 1: Write → Read Flow`);
  
  const testItem = {
    user_id: userId,
    title: `Test Item ${Date.now()}`,
    description: 'This is a test item for vault pipeline validation',
    url: `https://example.com/test/${Date.now()}`,
    content: 'Test content for enrichment. This should generate embeddings and extract topics like testing, validation, and pipeline.',
  };
  
  // Save via vault writer
  console.log(`${TEST_PREFIX} Saving test item...`);
  const saved = await vaultWriterService.saveItem(testItem, {
    sourceTable: 'deepsearch.items',
    skipEnrichment: false,
    skipEmbedding: false,
  });
  
  console.log(`${TEST_PREFIX} ✓ Item saved:`, saved.id);
  
  // Read back via vault service
  console.log(`${TEST_PREFIX} Reading back via vault...`);
  const allItems = await vaultService.getAllItems(userId, { forceRefresh: true });
  const retrieved = allItems.find(i => i.id === saved.id);
  
  if (!retrieved) {
    throw new Error('Item not found in vault after save');
  }
  
  console.log(`${TEST_PREFIX} ✓ Item retrieved from vault`);
  
  // Validate enrichment
  const validation = vaultWriterService.validateEnrichment(retrieved);
  
  if (!validation.valid) {
    console.warn(`${TEST_PREFIX} ⚠ Item missing enrichment:`, validation.errors);
  } else {
    console.log(`${TEST_PREFIX} ✓ Item fully enriched`);
  }
  
  console.log(`${TEST_PREFIX} Item details:`, {
    id: retrieved.id,
    title: retrieved.title,
    hasEmbedding: !!retrieved.embedding,
    themes: retrieved.extractedThemes,
    topics: retrieved.microTopics,
    source: retrieved.originalSourceTable,
  });
  
  return { success: true, itemId: saved.id, enriched: validation.valid };
}

/**
 * Test 2: Multi-Source Aggregation
 */
async function testMultiSourceAggregation(userId) {
  console.log(`${TEST_PREFIX} Test 2: Multi-Source Aggregation`);
  
  const allItems = await vaultService.getAllItems(userId, { forceRefresh: true });
  
  console.log(`${TEST_PREFIX} Total items:`, allItems.length);
  
  // Count by source
  const sources = {};
  allItems.forEach(item => {
    sources[item.originalSourceTable] = (sources[item.originalSourceTable] || 0) + 1;
  });
  
  console.log(`${TEST_PREFIX} Items by source:`, sources);
  
  // Check for required fields
  const missingFields = {
    embedding: 0,
    themes: 0,
    topics: 0,
    userId: 0,
  };
  
  allItems.forEach(item => {
    if (!item.embedding) missingFields.embedding++;
    if (!item.extractedThemes || item.extractedThemes.length === 0) missingFields.themes++;
    if (!item.microTopics || item.microTopics.length === 0) missingFields.topics++;
    if (!item.userId) missingFields.userId++;
  });
  
  console.log(`${TEST_PREFIX} Missing fields:`, missingFields);
  
  const enrichmentRate = ((allItems.length - missingFields.embedding) / allItems.length * 100).toFixed(1);
  console.log(`${TEST_PREFIX} Enrichment rate: ${enrichmentRate}%`);
  
  return {
    success: true,
    totalItems: allItems.length,
    sources,
    missingFields,
    enrichmentRate: parseFloat(enrichmentRate),
  };
}

/**
 * Test 3: Smart Insights Integration
 */
async function testSmartInsightsIntegration(userId) {
  console.log(`${TEST_PREFIX} Test 3: Smart Insights Integration`);
  
  // Import dynamically to avoid circular deps
  const suggestionsService = await import('../services/suggestionsService').then(m => m.default);
  
  const allItems = await vaultService.getAllItems(userId, { forceRefresh: true });
  console.log(`${TEST_PREFIX} Items available for insights:`, allItems.length);
  
  const insights = await suggestionsService.detectAllInsights(allItems);
  console.log(`${TEST_PREFIX} Insights detected:`, insights.length);
  
  insights.forEach(insight => {
    console.log(`${TEST_PREFIX}   - ${insight.type}: ${insight.title}`);
  });
  
  return {
    success: true,
    itemsUsed: allItems.length,
    insightsGenerated: insights.length,
    insightTypes: insights.map(i => i.type),
  };
}

/**
 * Test 4: Memory Surfacing Integration
 */
async function testMemorySurfacingIntegration(userId) {
  console.log(`${TEST_PREFIX} Test 4: Memory Surfacing Integration`);
  
  const memorySurfacingService = await import('../services/memorySurfacingService').then(m => m.default);
  
  const cards = await memorySurfacingService.detectAllCards(userId, { forceRefresh: true });
  console.log(`${TEST_PREFIX} Memory cards detected:`, cards.length);
  
  cards.forEach(card => {
    console.log(`${TEST_PREFIX}   - ${card.type}: ${card.title}`);
  });
  
  return {
    success: true,
    cardsGenerated: cards.length,
    cardTypes: cards.map(c => c.type),
  };
}

/**
 * Test 5: Cache Invalidation
 */
async function testCacheInvalidation(userId) {
  console.log(`${TEST_PREFIX} Test 5: Cache Invalidation`);
  
  // Read to populate cache
  console.log(`${TEST_PREFIX} Reading to populate cache...`);
  const items1 = await vaultService.getAllItems(userId);
  const count1 = items1.length;
  console.log(`${TEST_PREFIX} Initial count:`, count1);
  
  // Write a new item (should invalidate cache)
  console.log(`${TEST_PREFIX} Writing new item...`);
  const testItem = {
    user_id: userId,
    title: `Cache Test ${Date.now()}`,
    url: `https://example.com/cache-test/${Date.now()}`,
  };
  
  await vaultWriterService.saveItem(testItem, {
    sourceTable: 'deepsearch.items',
    skipEnrichment: true,
    skipEmbedding: true,
  });
  
  // Read again (should have new item)
  console.log(`${TEST_PREFIX} Reading after write...`);
  const items2 = await vaultService.getAllItems(userId);
  const count2 = items2.length;
  console.log(`${TEST_PREFIX} New count:`, count2);
  
  if (count2 <= count1) {
    console.warn(`${TEST_PREFIX} ⚠ Cache may not have been invalidated (count: ${count1} → ${count2})`);
    return { success: false, reason: 'Cache not invalidated' };
  }
  
  console.log(`${TEST_PREFIX} ✓ Cache properly invalidated`);
  return { success: true, itemsAdded: count2 - count1 };
}

/**
 * Run all tests
 */
export async function runAllTests(userId = null) {
  console.log('');
  console.log('═══════════════════════════════════════════');
  console.log('   VAULT PIPELINE TEST SUITE');
  console.log('═══════════════════════════════════════════');
  console.log('');
  
  try {
    // Get user ID
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      userId = user?.id;
    }
    
    if (!userId) {
      throw new Error('No user ID provided and not logged in');
    }
    
    console.log(`${TEST_PREFIX} User ID:`, userId.slice(0, 8));
    console.log('');
    
    const results = {};
    
    // Test 1
    try {
      results.writeRead = await testWriteReadFlow(userId);
    } catch (error) {
      results.writeRead = { success: false, error: error.message };
      console.error(`${TEST_PREFIX} ✗ Test 1 failed:`, error);
    }
    console.log('');
    
    // Test 2
    try {
      results.aggregation = await testMultiSourceAggregation(userId);
    } catch (error) {
      results.aggregation = { success: false, error: error.message };
      console.error(`${TEST_PREFIX} ✗ Test 2 failed:`, error);
    }
    console.log('');
    
    // Test 3
    try {
      results.smartInsights = await testSmartInsightsIntegration(userId);
    } catch (error) {
      results.smartInsights = { success: false, error: error.message };
      console.error(`${TEST_PREFIX} ✗ Test 3 failed:`, error);
    }
    console.log('');
    
    // Test 4
    try {
      results.memorySurfacing = await testMemorySurfacingIntegration(userId);
    } catch (error) {
      results.memorySurfacing = { success: false, error: error.message };
      console.error(`${TEST_PREFIX} ✗ Test 4 failed:`, error);
    }
    console.log('');
    
    // Test 5
    try {
      results.cacheInvalidation = await testCacheInvalidation(userId);
    } catch (error) {
      results.cacheInvalidation = { success: false, error: error.message };
      console.error(`${TEST_PREFIX} ✗ Test 5 failed:`, error);
    }
    console.log('');
    
    // Summary
    console.log('═══════════════════════════════════════════');
    console.log('   TEST RESULTS');
    console.log('═══════════════════════════════════════════');
    
    const testNames = {
      writeRead: 'Write → Read Flow',
      aggregation: 'Multi-Source Aggregation',
      smartInsights: 'Smart Insights Integration',
      memorySurfacing: 'Memory Surfacing Integration',
      cacheInvalidation: 'Cache Invalidation',
    };
    
    let passed = 0;
    let failed = 0;
    
    Object.entries(results).forEach(([key, result]) => {
      const status = result.success ? '✓ PASS' : '✗ FAIL';
      const name = testNames[key];
      console.log(`${status} - ${name}`);
      
      if (result.success) passed++;
      else failed++;
    });
    
    console.log('');
    console.log(`Total: ${passed} passed, ${failed} failed`);
    console.log('═══════════════════════════════════════════');
    console.log('');
    
    return {
      passed,
      failed,
      results,
    };
    
  } catch (error) {
    console.error(`${TEST_PREFIX} Test suite failed:`, error);
    throw error;
  }
}

// Expose globally
if (typeof window !== 'undefined') {
  window.testVaultPipeline = runAllTests;
  console.log('🧪 Vault test suite loaded: window.testVaultPipeline()');
}

export default {
  runAllTests,
  testWriteReadFlow,
  testMultiSourceAggregation,
  testSmartInsightsIntegration,
  testMemorySurfacingIntegration,
  testCacheInvalidation,
};





